import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { isSameHour } from 'date-fns';
import UPDATE_POST_DATE from '../graphql/updatePostDate';
import GET_CALENDAR_WEEK_QUERY from '../graphql/week';
import getFormattedTime from '../../utils/getFormattedTime';

const reorderPosts = (days, updatedPost) => {
  const daysAfterRemove = days.map(day => {
    const intervals = day.intervals.map(interval => {
      const { posts } = interval;
      return {
        ...interval,
        posts: posts.filter(post => post.id !== updatedPost.id),
      };
    });
    return {
      ...day,
      intervals,
    };
  });

  return daysAfterRemove.map(day => {
    const intervals = day.intervals.map(interval => {
      // TODO: account for time intervals, with this approach we are assuming the interval is 1 hour
      if (
        isSameHour(interval.startTimestamp * 1000, updatedPost.dueAt * 1000)
      ) {
        return {
          ...interval,
          posts: [...interval.posts, updatedPost],
        };
      }
      return interval;
    });
    return {
      ...day,
      intervals,
    };
  });
};

export default function useUpdateWeekPostDate({
  onSuccess,
  onError,
  variables,
}) {
  const [updatePostDueAt, { data, error: mutationError }] = useMutation(
    UPDATE_POST_DATE,
    {
      update: (cache, mutationResult) => {
        const updatedPost = mutationResult.data.updatePostDueAt.post;
        const readData = cache.readQuery({
          query: GET_CALENDAR_WEEK_QUERY,
          variables,
        });

        const { days, posts } = readData.calendar;
        const postsReordered = reorderPosts(
          days,
          updatedPost
        );

        cache.writeQuery({
          query: GET_CALENDAR_WEEK_QUERY,
          variables,
          data: {
            calendar: { __typename: 'Calendar', posts, days: postsReordered },
          },
        });
      },
      refetchQueries: [GET_CALENDAR_WEEK_QUERY],
    }
  );

  const [error, setError] = useState(null);

  useEffect(() => {
    if (mutationError) {
      setError(mutationError);
    }

    if (data?.updatePostDueAt?.success) {
      onSuccess();
    }

    if (data?.updatePostDueAt?.success === false) {
      setError({ message: data?.updatePostDueAt.message });
    }
  }, [data, mutationError]);

  useEffect(() => {
    if (error) {
      onError();
    }
  }, [error]);

  return {
    error,
    result: data,
    updatePostDueAt: ({ post, timestamp }) => {
      const formatted = getFormattedTime(
        timestamp * 1000,
        variables.timezone,
        variables.use24HourTime
      );

      updatePostDueAt({
        variables: { input: { postId: post.id, timestamp } },
        optimisticResponse: {
          updatePostDueAt: {
            success: true,
            message: 'Successfully updated post',
            post: {
              ...post,
              dueAt: timestamp,
              formattedTime: formatted,
            },
            __typename: 'PostUpdated',
          },
        },
      }).catch(e => console.error(e));
    },
  };
}
